/**
 * Shared form-state types for the "Add Prescription via NewRx" flow (IngestNewRxBatch).
 *
 * One NewRxItemEntry maps to one NewRxBatchItem in the request. Fields are held as strings for
 * controlled Polaris inputs; integer medication fields are parsed at submit time (Task 8).
 */
import { PRESCRIBERS } from "../../../../../prescription_ingestion/config/PrescriptionDefaults";
import type { PrescriptionIngestionStep } from "@amzn/apaqportaljavalambdatypescriptclient";

export type { PrescriptionIngestionStep };

/**
 * Allowed Quantity Unit of Measure options for the NewRx medication form (NCI Thesaurus codes).
 *
 * SOURCE OF TRUTH: `ApaqPortalJavaLambda/src/com/amazon/apaqportaljavalambda/util/NcpdpQuantityUnitOfMeasure.java`
 * (one option per distinct supported NCI code; the backend validates the submitted value against that exact
 * set). If the Lambda map gains/loses a code, update this list to match. Labels are the canonical form name.
 */
export interface QuantityUomOption {
  /** NCI Thesaurus code sent to the backend, e.g. "C48542". */
  value: string;
  /** Human-readable form name, e.g. "Tablet". */
  label: string;
}

export const QUANTITY_UOM_OPTIONS: QuantityUomOption[] = [
  { value: "C48542", label: "Tablet" },
  { value: "C48480", label: "Capsule" },
  { value: "C28254", label: "Milliliter" },
  { value: "C28253", label: "Milligram" },
  { value: "C48155", label: "Gram" },
  { value: "C48152", label: "Microgram" },
  { value: "C64933", label: "Each" },
  { value: "C48551", label: "Vial" },
  { value: "C48540", label: "Syringe" },
  { value: "C48524", label: "Patch" },
  { value: "C48539", label: "Suppository" },
  { value: "C53499", label: "Film" },
  { value: "C48538", label: "Strip" },
  { value: "C48506", label: "Lozenge" },
  { value: "C69124", label: "Gum" },
  { value: "C64696", label: "Caplet" },
  { value: "C48552", label: "Wafer" },
  { value: "C48521", label: "Packet" },
  { value: "C48579", label: "International Unit" },
  { value: "C62275", label: "Inhaler" },
  { value: "C71204", label: "Nebule" },
  { value: "C62276", label: "Insert" },
  { value: "C62609", label: "Ring" },
  { value: "C62412", label: "Applicator" },
  { value: "C65032", label: "Pad" },
  { value: "C120216", label: "Pen Needle" },
  { value: "C120263", label: "Lancet" },
  { value: "C48499", label: "Implant" },
  { value: "C48504", label: "Kit" },
  { value: "C48548", label: "Troche" },
  { value: "C54564", label: "Blister" },
  { value: "C53502", label: "Sponge" },
  { value: "C53503", label: "Stick" },
  { value: "C53504", label: "Swab" },
  { value: "C97717", label: "Pen" },
  { value: "C38046", label: "Unspecified" },
];

const QUANTITY_UOM_CODES = new Set(QUANTITY_UOM_OPTIONS.map((o) => o.value));

/** True when the stored value is one of the supported NCI codes (not free text / display text). */
export function isSupportedQuantityUom(value: string): boolean {
  return QUANTITY_UOM_CODES.has(value.trim());
}

/** Default prescriber preset key for a new item. Aarti Kulkarni (Beverly Hills). */
const DEFAULT_PRESCRIBER_KEY = "dr_kulkarni";

/** Mirrors NewRxPrescriber. `useSpi` selects between prescriberSpi and the inline prescriber. */
export interface NewRxPrescriberFormState {
  useSpi: boolean;
  prescriberSpi: string;
  // Required NewRxPrescriber fields (only when useSpi is false)
  npi: string;
  deaNumber: string;
  firstName: string;
  lastName: string;
  addressLine1: string;
  city: string;
  state: string;
  postalCode: string;
  // Optional NewRxPrescriber fields
  stateLicenseNumber: string;
  suffix: string;
  specialty: string;
  practiceName: string;
  countryCode: string;
  phone: string;
  fax: string;
  email: string;
}

/** Mirrors NewRxMedication. quantity/daysSupply/numberOfRefills/sigText always required. */
export interface NewRxMedicationFormState {
  // Always required
  quantity: string;
  daysSupply: string;
  numberOfRefills: string;
  sigText: string;
  // Required when the item has no drugId
  ndc: string;
  quantityUnitOfMeasure: string;
  // Advanced / optional
  drugDescription: string;
  drugDbCode: string;
  drugDbQualifier: string;
  strengthValue: string;
  quantityCodeListQualifier: string;
  substitutions: string;
  writtenDate: string;
}

/** How the drug is identified for an item: by drug ID (server resolves NDC) or by raw NDC. */
export type DrugSource = "drugId" | "ndc";

/** One tab = one NewRxBatchItem. */
export interface NewRxItemEntry {
  drugSource: DrugSource;
  drugId: string;
  drugIdInput: string; // Autosuggest display text (reuses PrescriptionDetailsBlock contract)
  prescriber: NewRxPrescriberFormState;
  medication: NewRxMedicationFormState;
  stopAtStep: PrescriptionIngestionStep;
}

export const EMPTY_PRESCRIBER: NewRxPrescriberFormState = {
  useSpi: true,
  prescriberSpi: "",
  npi: "",
  deaNumber: "",
  firstName: "",
  lastName: "",
  addressLine1: "",
  city: "",
  state: "",
  postalCode: "",
  stateLicenseNumber: "",
  suffix: "",
  specialty: "",
  practiceName: "",
  countryCode: "",
  phone: "",
  fax: "",
  email: "",
};

/**
 * Default prescriber for a new item — the DEFAULT_PRESCRIBER_KEY preset, mapped the same way as
 * NewRxPrescriberBlock.applyPreset. Starts in SPI mode with inline details also populated so the
 * selection survives a mode switch. Falls back to an empty prescriber if the preset is missing.
 */
export const DEFAULT_PRESCRIBER: NewRxPrescriberFormState = (() => {
  const p = PRESCRIBERS[DEFAULT_PRESCRIBER_KEY];
  if (!p) return { ...EMPTY_PRESCRIBER };
  return {
    ...EMPTY_PRESCRIBER,
    useSpi: true,
    prescriberSpi: p.spi,
    npi: p.npi,
    deaNumber: p.deaNumber,
    firstName: p.firstName,
    lastName: p.lastName,
    addressLine1: p.addressLine1,
    city: p.city,
    state: p.state,
    postalCode: p.zipCode,
    practiceName: p.clinicName,
    phone: p.phoneNumber,
    fax: p.fax && p.fax.length > 0 ? p.fax[0].Number : "",
  };
})();

export const EMPTY_MEDICATION: NewRxMedicationFormState = {
  quantity: "30",
  daysSupply: "30",
  numberOfRefills: "5",
  sigText: "Take 1 tablet by mouth daily.",
  ndc: "",
  quantityUnitOfMeasure: "",
  drugDescription: "",
  drugDbCode: "",
  drugDbQualifier: "",
  strengthValue: "",
  quantityCodeListQualifier: "",
  substitutions: "",
  writtenDate: "",
};

export const EMPTY_ITEM: NewRxItemEntry = {
  drugSource: "drugId",
  drugId: "",
  drugIdInput: "",
  prescriber: { ...DEFAULT_PRESCRIBER },
  medication: { ...EMPTY_MEDICATION },
  stopAtStep: "RPH_APPROVAL",
};
